import clsx from 'clsx'
import {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import Draggable from 'react-draggable'
import { useBehaviorMapper } from 'src/hooks'
import { IconDragDrop, IconPause, IconPlay } from 'src/icons'
import { RecorderModule } from '../modal-recorder/recorder.module'
import Style from './style.module.scss'

const fonts = [12, 14, 16]

const speeds = [70, 50, 30, 20, 10]

const mappingSpeedToText = (speed: number) => {
  switch (speed) {
    case 70:
      return '0.25'
    case 50:
      return '0.5'
    case 30:
      return '1x'
    case 20:
      return '1.25'
    case 10:
      return '1.5'
    default:
      return '1x'
  }
}

interface IProps extends ComponentProps<'textarea'> {
  wrapperProps?: ComponentProps<'div'>
  defaultPosition?: {
    x: number
    y: number
  }
  startRecording?: boolean
}

export const TeleprompterTextarea: FC<IProps> = ({ wrapperProps, defaultPosition, startRecording, ...props }) => {
  const teleprompter = useBehaviorMapper(RecorderModule.teleprompter$)
  const [fontSize, setFontSize] = useState(14)
  const [playing, setPlaying] = useState(false)
  const teleRef = useRef<HTMLTextAreaElement>(null)
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const handleNextSpeed = useCallback(() => {
    const currentIndex = speeds.indexOf(teleprompter.speed)
    const nextIndex = (currentIndex + 1) % speeds.length
    const nextSpeed = speeds[nextIndex]
    RecorderModule.setTeleprompter({ speed: nextSpeed })
  }, [teleprompter.speed])

  const scrollToTop = () => {
    teleRef?.current?.scroll({
      top: 0,
      behavior: 'smooth'
    })
  }

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    if (startRecording) {
      scrollToTop()

      timeoutId = setTimeout(() => {
        setPlaying(true)
      }, 3000)
    } else {
      setPlaying(false)
    }

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
    }
  }, [startRecording])

  useEffect(() => {
    if (!teleRef.current) {
      return
    }

    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }

    if (window === undefined) {
      return
    }

    const scrollHeight = teleRef.current.scrollHeight - teleRef.current.clientHeight

    if (!playing) {
      return
    }

    intervalIdRef.current = setInterval(() => {
      if ((teleRef.current?.scrollTop || 0) <= scrollHeight) {
        teleRef.current?.scrollTo(0, teleRef.current?.scrollTop + 1)
      }
    }, teleprompter.speed)

    return () => {
      if (intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current)
      }
    }
  }, [teleprompter.speed, playing])

  return createPortal((
    <Draggable
      handle="#dnd-icon"
      bounds="body"
      onStart={() => {
        teleRef.current && (teleRef.current.style.opacity = '0.99')
      }}
      onStop={() => {
        teleRef.current && (teleRef.current.style.opacity = '1')
      }}
    >
      <div
        {...wrapperProps}
        className={clsx(wrapperProps?.className, Style.wrapper)}
        style={{
          left: defaultPosition?.x || undefined,
          top: defaultPosition?.y || undefined,
          right: defaultPosition?.x ? undefined : 0,
          bottom: defaultPosition?.y ? undefined : 0
        }}
      >
        <textarea
          {...props}
          ref={teleRef}
          className={clsx(`round-4 body-${fontSize}`, props.className, Style.textarea)}
          placeholder={props.placeholder || 'Add your notes here'}
        />

        <div className={Style.iconDragWrapper}>
          <IconDragDrop
            id="dnd-icon"
            className={clsx(Style.iconDrag, 'txt-grey-03 hover-txt-neutral-white')}
          />
          {teleprompter.text && (
            <button
              onClick={() => setPlaying(!playing)}
              className={clsx(
                'fx fx-center round-3 txt-neutral-white pointer',
                Style.play
              )}
            >
              {playing
                ? <IconPause className={Style.iconPlay}/>
                : <IconPlay className={Style.iconPlay}/>}
            </button>
          )}
        </div>

        <div className={Style.fontsWrapper}>
          <div className={clsx('fx fx-ai-baseline gap-3', Style.fonts)}>
            {fonts.map((size) => (
              <span
                key={size}
                className={clsx(Style.font, `fs-${size} pointer`, fontSize === size && Style.active)}
                onClick={() => setFontSize(size)}
                style={{
                  width: size + 4,
                  height: size + 4
                }}
              >
                a
              </span>
            ))}
          </div>
          {!!teleprompter.text && (
            <button
              onClick={handleNextSpeed}
              className={clsx(
                'm-0 p-0 round-3 pointer fw-bold fs-12',
                Style.speed
              )}
            >
              {mappingSpeedToText(teleprompter.speed)}
            </button>
          )}
        </div>
      </div>
    </Draggable>
  ), document.body)
}
